import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import { carregarDetalheAnuncios, type DetalheAnuncios as Detalhe } from '../api/detalheAnuncios'
import type { FiltroTrafego } from '../api/trafegoPago'
import { useAuth } from '../state/AuthContext'
import { PLATAFORMAS_ALTERAVEIS } from '../utils/alteracoes'
import { AplicarAlteracao, type PedidoAlteracao } from './AplicarAlteracao'
import { formatarConversoes, formatarInteiro, formatarPercentual, formatarPosicao, formatarReais, razao } from '../utils/formato'

type Aba = 'conjuntos' | 'anuncios' | 'posicionamentos'
const LIMITE = 20
/** Acima disso, a frequência de 7 dias costuma indicar anúncio cansado (referência de mercado para o Meta). */
export const FREQUENCIA_ALTA = 3

const STATUS: Record<string, string> = {
  ACTIVE: 'Ativo',
  ENABLED: 'Ativo',
  PAUSED: 'Pausado',
  CAMPAIGN_PAUSED: 'Campanha pausada',
  ADSET_PAUSED: 'Conjunto pausado',
  ARCHIVED: 'Arquivado',
  DELETED: 'Apagado',
  REMOVED: 'Removido',
  WITH_ISSUES: 'Com problema',
  IN_PROCESS: 'Em processamento',
  PENDING_REVIEW: 'Em revisão',
  DISAPPROVED: 'Reprovado',
}
const status = (s: string | null) => (s ? (STATUS[s] ?? s.toLowerCase()) : '—')
const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
const traco = (v: number | null, f: (x: number) => string) => (v == null ? '—' : f(v))
const cpm = (inv: number, imp: number) => traco(razao(inv * 1000, imp), formatarReais)
const ctr = (cli: number, imp: number) => traco(razao(cli, imp), formatarPercentual)

/**
 * O "por dentro" das campanhas: conjuntos, anúncios (com a frequência de 7 dias) e
 * posicionamentos, no mesmo recorte do painel. CPM e CTR saem das somas do período.
 */
export function DetalheAnuncios({ filtro }: { filtro: FiltroTrafego }) {
  const [dados, setDados] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [aba, setAba] = useState<Aba>('anuncios')
  const [todos, setTodos] = useState(false)
  const [menu, setMenu] = useState<{ el: HTMLElement; plataforma: string; nivel: 'conjunto' | 'anuncio'; id: string; nome: string; status: string | null } | null>(null)
  const [pedido, setPedido] = useState<PedidoAlteracao | null>(null)
  const { pode } = useAuth()
  // Alterar pelo sistema: só quem tem a permissão, só anúncio e conjunto aqui (campanha é pelas sugestões).
  const alteravel = pode('campanhas.alterar') && aba !== 'posicionamentos'
  const chave = JSON.stringify(filtro)

  useEffect(() => {
    let cancelado = false
    setErro(null)
    carregarDetalheAnuncios(JSON.parse(chave) as FiltroTrafego)
      .then((d) => !cancelado && setDados(d))
      .catch((e: Error) => !cancelado && setErro(e.message))
    return () => {
      cancelado = true
    }
  }, [chave])

  const vazio = dados && !dados.conjuntos.length && !dados.anuncios.length && !dados.posicionamentos.length
  const totalPos = (dados?.posicionamentos ?? []).reduce((s, p) => s + p.investimento, 0)
  const linhas = dados ? { conjuntos: dados.conjuntos, anuncios: dados.anuncios, posicionamentos: dados.posicionamentos }[aba] : []

  return (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 2, pt: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
          Por dentro das campanhas
        </Typography>
        <Tabs value={aba} onChange={(_, v) => (setAba(v), setTodos(false))} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}>
          <Tab value="anuncios" label={`Anúncios${dados ? ` (${dados.anuncios.length})` : ''}`} />
          <Tab value="conjuntos" label={`Conjuntos${dados ? ` (${dados.conjuntos.length})` : ''}`} />
          <Tab value="posicionamentos" label="Posicionamentos" />
        </Tabs>
      </Stack>

      {erro ? (
        <Typography variant="body2" color="error" sx={{ p: 2 }}>
          {erro}
        </Typography>
      ) : !dados ? (
        <LinearProgress sx={{ m: 2, borderRadius: 1 }} />
      ) : vazio ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Sem dados por conjunto e anúncio neste recorte. Eles entram com a coleta do Meta Ads e do Google Ads; para trazer o histórico, use
          “Coletar agora” com 90 dias em Configurações → Marcas.
        </Typography>
      ) : (
        <>
          {/* position relative: o que for absoluto dentro da tabela fica contido nela (e rola junto). */}
          <TableContainer sx={{ position: 'relative' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{aba === 'anuncios' ? 'Anúncio' : aba === 'conjuntos' ? 'Conjunto' : 'Posicionamento'}</TableCell>
                  {aba === 'posicionamentos' ? <TableCell align="right">% da verba</TableCell> : <TableCell>Situação</TableCell>}
                  <TableCell align="right">Investimento</TableCell>
                  <TableCell align="right">CPM</TableCell>
                  <TableCell align="right">CTR</TableCell>
                  <TableCell align="right">Conversões</TableCell>
                  <TableCell align="right">Custo por conversão</TableCell>
                  {aba === 'anuncios' ? <TableCell align="right">Frequência (7 dias)</TableCell> : null}
                  {alteravel ? (
                    <TableCell padding="checkbox">
                      {/* Só para leitor de tela. Em px: no sx do MUI, width: 1 é 100% (e esticava a página para o lado). */}
                      <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
                        Ações
                      </Box>
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {(todos ? linhas : linhas.slice(0, LIMITE)).map((l) => {
                  const a = aba === 'anuncios' ? (l as Detalhe['anuncios'][number]) : null
                  const c = aba === 'conjuntos' ? (l as Detalhe['conjuntos'][number]) : null
                  return (
                    <TableRow key={a?.id ?? c?.id ?? `${l.nome}-${l.plataforma}`} hover>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                          {l.nome}
                        </Typography>
                        {a || c ? (
                          <Typography variant="caption" color="text.secondary">
                            {a ? `${a.conjunto} · ${a.campanha}${a.formato ? ` · ${a.formato}` : ''}` : c!.campanha}
                          </Typography>
                        ) : null}
                      </TableCell>
                      {aba === 'posicionamentos' ? (
                        <TableCell align="right" sx={num}>
                          {traco(razao(l.investimento, totalPos), formatarPercentual)}
                        </TableCell>
                      ) : (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{status((a ?? c)!.status)}</TableCell>
                      )}
                      <TableCell align="right" sx={num}>
                        {formatarReais(l.investimento)}
                      </TableCell>
                      <TableCell align="right" sx={num}>
                        {cpm(l.investimento, l.impressoes)}
                      </TableCell>
                      <TableCell align="right" sx={num}>
                        {ctr(l.cliques, l.impressoes)}
                      </TableCell>
                      <TableCell align="right" sx={num}>
                        {formatarConversoes(l.conversoes)}
                      </TableCell>
                      <TableCell align="right" sx={num}>
                        {traco(razao(l.investimento, l.conversoes), formatarReais)}
                      </TableCell>
                      {a ? (
                        <TableCell align="right" sx={num}>
                          {a.frequencia == null ? (
                            '—'
                          ) : (
                            <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                              {a.frequencia >= FREQUENCIA_ALTA ? <Chip size="small" color="warning" variant="outlined" label="alta" /> : null}
                              <span>{formatarPosicao(a.frequencia)}</span>
                            </Stack>
                          )}
                        </TableCell>
                      ) : null}
                      {alteravel ? (
                        <TableCell padding="checkbox">
                          {PLATAFORMAS_ALTERAVEIS[(a ?? c)!.plataforma] ? (
                            <IconButton
                              size="small"
                              aria-label={`Alterar ${l.nome}`}
                              onClick={(ev) => setMenu({ el: ev.currentTarget, plataforma: (a ?? c)!.plataforma, nivel: a ? 'anuncio' : 'conjunto', id: (a ?? c)!.id, nome: l.nome, status: (a ?? c)!.status })}
                            >
                              ⋯
                            </IconButton>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
            {linhas.length > LIMITE ? (
              <Button size="small" onClick={() => setTodos((t) => !t)} sx={{ textTransform: 'none' }}>
                {todos ? `Mostrar os ${LIMITE} maiores` : `Mostrar todos (${formatarInteiro(linhas.length)})`}
              </Button>
            ) : null}
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                {aba === 'anuncios'
                  ? `Frequência: vezes que cada pessoa viu o anúncio nos últimos 7 dias da coleta; “alta” a partir de ${FREQUENCIA_ALTA}.`
                  : 'CPM = custo por mil impressões · CTR = cliques ÷ impressões.'}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
      <Menu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)}>
        {menu
          ? [
              menu.status === 'PAUSED' ? (
                <MenuItem key="reativar" onClick={() => (setPedido({ plataforma: menu.plataforma, nivel: menu.nivel, objetoId: menu.id, nome: menu.nome, tipo: 'reativar', percentual: null }), setMenu(null))}>
                  Reativar
                </MenuItem>
              ) : (
                <MenuItem key="pausar" onClick={() => (setPedido({ plataforma: menu.plataforma, nivel: menu.nivel, objetoId: menu.id, nome: menu.nome, tipo: 'pausar', percentual: null }), setMenu(null))}>
                  Pausar
                </MenuItem>
              ),
              // No Google o grupo de anúncios não tem orçamento: é da campanha.
              menu.nivel === 'conjunto' && menu.plataforma === 'meta_ads' ? (
                <MenuItem key="orcamento" onClick={() => (setPedido({ plataforma: 'meta_ads', nivel: 'conjunto', objetoId: menu.id, nome: menu.nome, tipo: 'orcamento', percentual: 20 }), setMenu(null))}>
                  Ajustar orçamento
                </MenuItem>
              ) : null,
            ]
          : null}
      </Menu>
      {pedido ? <AplicarAlteracao key={JSON.stringify(pedido)} pedido={pedido} origem="manual" onFechar={() => setPedido(null)} /> : null}
    </Paper>
  )
}
